/**
 * KG drawer API facade — live HTTP by default via global `api.ts` registry.
 *
 * Toggle: REACT_APP_KG_DRAWER_API=mock (default: live)
 */

import { isMockApiFlag } from "../apiMode";
import { kgDrawerService } from "./kgDrawer.service";
import {
  parseArtifactAssetId,
  parseDataModelAssetId,
  parseDataSourceAssetId,
  parseMetricAssetId,
  parseOntologyDomainId,
  parseReportAssetId,
  parseSchemaAssetId,
  parseStoryAssetId,
} from "./kgNodeIds";
import type {
  KgDataSourceReportsResponse,
  KgDataSourceTablesResponse,
  KgDataSourceUsedInParams,
  KgDataSourceUsedInResponse,
  KgDrawerListParams,
  KgNodeOverviewResponse,
  KgOntologyLinksResponse,
  KgOntologyMetricsResponse,
  KgOntologyOverviewResponse,
  KgOntologyReportsResponse,
  KgDataModelMetricsResponse,
  KgDataModelOverviewResponse,
  KgDataModelRelationshipsResponse,
  KgDataModelTablesResponse,
  KgOntologyRulesResponse,
  KgMetricFormulaResponse,
  KgMetricOverviewResponse,
  KgDataStoryActivityResponse,
  KgDataStoryOverviewResponse,
  KgDataStoryTraceResponse,
  KgArtifactActivityResponse,
  KgArtifactOverviewResponse,
  KgReportActivityResponse,
  KgReportOverviewResponse,
  KgReportTraceResponse,
  KgTableOverviewResponse,
  KgSchemaMetricsResponse,
  KgSchemaRelationshipsParams,
  KgSchemaRelationshipsResponse,
  KgSchemaUsedInResponse,
} from "./kgDrawerApi.types";
import {
  mockFetchDataSourceReports,
  mockFetchDataSourceTables,
  mockFetchDataSourceUsedIn,
  mockFetchNodeOverview,
  mockFetchOntologyLinks,
  mockFetchOntologyMetrics,
  mockFetchDataModelMetrics,
  mockFetchDataModelOverview,
  mockFetchDataModelRelationships,
  mockFetchDataModelTables,
  mockFetchOntologyOverview,
  mockFetchOntologyReports,
  mockFetchOntologyRules,
  mockFetchOntologyUsedIn,
  mockFetchMetricFormula,
  mockFetchMetricOverview,
  mockFetchMetricUsedIn,
  mockFetchDataStoryActivity,
  mockFetchDataStoryOverview,
  mockFetchDataStoryTrace,
  mockFetchArtifactActivity,
  mockFetchArtifactOverview,
  mockFetchReportActivity,
  mockFetchReportOverview,
  mockFetchReportTrace,
  mockFetchReportUsedIn,
  mockFetchTableOverview,
  mockFetchSchemaMetrics,
  mockFetchSchemaRelationships,
  mockFetchSchemaUsedIn,
} from "./kgDrawerApi.mock";

const isMockDrawerApi = () =>
  isMockApiFlag(process.env.REACT_APP_KG_DRAWER_API);

/** Data-source node id is canvas `dataSource-{uuid}`; live API uses raw UUID (BE resolves id type). */
export const fetchNodeOverview = async (
  nodeId: string,
): Promise<KgNodeOverviewResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchNodeOverview(nodeId);
  }
  return kgDrawerService.getDataSourceOverview(parseDataSourceAssetId(nodeId));
};

/** Table/schema node id is the canvas id (`schema-{uuid}`); live API uses raw UUID. */
export const fetchTableOverview = async (
  schemaId: string,
): Promise<KgTableOverviewResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchTableOverview(schemaId);
  }
  return kgDrawerService.getTableOverview(parseSchemaAssetId(schemaId));
};

/** Data-source node id is canvas `dataSource-{uuid}`; live API uses raw UUID. */
export const fetchDataSourceTables = async (
  dataSourceId: string,
  params?: KgDrawerListParams,
): Promise<KgDataSourceTablesResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchDataSourceTables(dataSourceId, params);
  }
  return kgDrawerService.getDataSourceTables(
    parseDataSourceAssetId(dataSourceId),
    params,
  );
};

/** Data-source node id is canvas `dataSource-{uuid}`; live API uses raw UUID. */
export const fetchDataSourceReports = async (
  dataSourceId: string,
  params?: KgDrawerListParams,
): Promise<KgDataSourceReportsResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchDataSourceReports(dataSourceId, params);
  }
  return kgDrawerService.getDataSourceReports(
    parseDataSourceAssetId(dataSourceId),
    params,
  );
};

/** Data-source node id is canvas `dataSource-{uuid}`; live API uses raw UUID. */
export const fetchDataSourceUsedIn = async (
  dataSourceId: string,
  params?: KgDataSourceUsedInParams,
): Promise<KgDataSourceUsedInResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchDataSourceUsedIn(dataSourceId);
  }
  return kgDrawerService.getDataSourceUsedIn(
    parseDataSourceAssetId(dataSourceId),
    params,
  );
};

/** Table/schema node id is the canvas id (`schema-{uuid}`); live API uses raw UUID. */
export const fetchSchemaRelationships = async (
  schemaId: string,
  params?: KgSchemaRelationshipsParams,
): Promise<KgSchemaRelationshipsResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchSchemaRelationships(schemaId, params);
  }
  return kgDrawerService.getSchemaRelationships(
    parseSchemaAssetId(schemaId),
    params,
  );
};

/** Table/schema node id is the canvas id (`schema-{uuid}`); live API uses raw UUID. */
export const fetchSchemaMetrics = async (
  schemaId: string,
  params?: KgDrawerListParams,
): Promise<KgSchemaMetricsResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchSchemaMetrics(schemaId, params);
  }
  return kgDrawerService.getSchemaMetrics(parseSchemaAssetId(schemaId), params);
};

/** Table/schema node id is the canvas id (`schema-{uuid}`); live API uses raw UUID. */
export const fetchSchemaUsedIn = async (
  schemaId: string,
  params?: KgDataSourceUsedInParams,
): Promise<KgSchemaUsedInResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchSchemaUsedIn(schemaId);
  }
  return kgDrawerService.getSchemaUsedIn(parseSchemaAssetId(schemaId), params);
};

/** Ontology node id is canvas `ontology-{uuid}`; live API uses raw KGOolDomain UUID. */
export const fetchOntologyOverview = async (
  ontologyId: string,
): Promise<KgOntologyOverviewResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchOntologyOverview(ontologyId);
  }
  return kgDrawerService.getOntologyOverview(parseOntologyDomainId(ontologyId));
};

/** Ontology node id is canvas `ontology-{uuid}`; live API uses raw KGOolDomain UUID. */
export const fetchOntologyLinks = async (
  ontologyId: string,
  params?: KgDrawerListParams,
): Promise<KgOntologyLinksResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchOntologyLinks(ontologyId, params);
  }
  return kgDrawerService.getOntologyLinks(
    parseOntologyDomainId(ontologyId),
    params,
  );
};

/** Ontology node id is canvas `ontology-{uuid}`; live API uses raw KGOolDomain UUID. */
export const fetchOntologyMetrics = async (
  ontologyId: string,
  params?: KgDrawerListParams,
): Promise<KgOntologyMetricsResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchOntologyMetrics(ontologyId, params);
  }
  return kgDrawerService.getOntologyMetrics(
    parseOntologyDomainId(ontologyId),
    params,
  );
};

/** Ontology node id is canvas `ontology-{uuid}`; live API uses raw KGOolDomain UUID. */
export const fetchOntologyRules = async (
  ontologyId: string,
): Promise<KgOntologyRulesResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchOntologyRules(ontologyId);
  }
  return kgDrawerService.getOntologyRules(parseOntologyDomainId(ontologyId));
};

/** Ontology node id is canvas `ontology-{uuid}`; live API uses raw KGOolDomain UUID. */
export const fetchOntologyReports = async (
  ontologyId: string,
  params?: KgDrawerListParams,
): Promise<KgOntologyReportsResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchOntologyReports(ontologyId, params);
  }
  return kgDrawerService.getOntologyReports(
    parseOntologyDomainId(ontologyId),
    params,
  );
};

/** Ontology node id is canvas `ontology-{uuid}`; live API uses raw KGOolDomain UUID. */
export const fetchOntologyUsedIn = async (
  ontologyId: string,
  params?: KgDataSourceUsedInParams,
): Promise<KgDataSourceUsedInResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchOntologyUsedIn(ontologyId);
  }
  return kgDrawerService.getOntologyUsedIn(
    parseOntologyDomainId(ontologyId),
    params,
  );
};

/** Metric node id is canvas `metric-{uuid}`; live API uses raw KGMetric UUID. */
export const fetchMetricOverview = async (
  metricId: string,
): Promise<KgMetricOverviewResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchMetricOverview(metricId);
  }
  return kgDrawerService.getMetricOverview(parseMetricAssetId(metricId));
};

/** Metric node id is canvas `metric-{uuid}`; live API uses raw KGMetric UUID. */
export const fetchMetricFormula = async (
  metricId: string,
): Promise<KgMetricFormulaResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchMetricFormula(metricId);
  }
  return kgDrawerService.getMetricFormula(parseMetricAssetId(metricId));
};

/** Metric node id is canvas `metric-{uuid}`; live API uses raw KGMetric UUID. */
export const fetchMetricUsedIn = async (
  metricId: string,
  params?: KgDataSourceUsedInParams,
): Promise<KgDataSourceUsedInResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchMetricUsedIn(metricId);
  }
  return kgDrawerService.getMetricUsedIn(parseMetricAssetId(metricId), params);
};

/** Report node id is canvas `report-{uuid}`; live API uses raw report UUID. */
export const fetchReportOverview = async (
  reportId: string,
): Promise<KgReportOverviewResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchReportOverview(reportId);
  }
  return kgDrawerService.getReportOverview(parseReportAssetId(reportId));
};

/** Report node id is canvas `report-{uuid}`; live API uses raw report UUID. */
export const fetchReportTrace = async (
  reportId: string,
): Promise<KgReportTraceResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchReportTrace(reportId);
  }
  return kgDrawerService.getReportTrace(parseReportAssetId(reportId));
};

/** Report node id is canvas `report-{uuid}`; live API uses raw report UUID. */
export const fetchReportUsedIn = async (
  reportId: string,
  params?: KgDataSourceUsedInParams,
): Promise<KgDataSourceUsedInResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchReportUsedIn(reportId);
  }
  return kgDrawerService.getReportUsedIn(parseReportAssetId(reportId), params);
};

/** Report node id is canvas `report-{uuid}`; live API uses raw report UUID. */
export const fetchReportActivity = async (
  reportId: string,
): Promise<KgReportActivityResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchReportActivity(reportId);
  }
  return kgDrawerService.getReportActivity(parseReportAssetId(reportId));
};

/** Data story node id is canvas `story-{uuid}`; live API uses raw data story UUID. */
export const fetchDataStoryOverview = async (
  dataStoryId: string,
): Promise<KgDataStoryOverviewResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchDataStoryOverview(dataStoryId);
  }
  return kgDrawerService.getDataStoryOverview(parseStoryAssetId(dataStoryId));
};

/** Data story node id is canvas `story-{uuid}`; live API uses raw data story UUID. */
export const fetchDataStoryTrace = async (
  dataStoryId: string,
): Promise<KgDataStoryTraceResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchDataStoryTrace(dataStoryId);
  }
  return kgDrawerService.getDataStoryTrace(parseStoryAssetId(dataStoryId));
};

/** Data story node id is canvas `story-{uuid}`; live API uses raw data story UUID. */
export const fetchDataStoryActivity = async (
  dataStoryId: string,
): Promise<KgDataStoryActivityResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchDataStoryActivity(dataStoryId);
  }
  return kgDrawerService.getDataStoryActivity(parseStoryAssetId(dataStoryId));
};

/** Artifact node id is canvas `artifact-{uuid}`; live API uses raw artifact UUID. */
export const fetchArtifactOverview = async (
  artifactId: string,
): Promise<KgArtifactOverviewResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchArtifactOverview(artifactId);
  }
  return kgDrawerService.getArtifactOverview(parseArtifactAssetId(artifactId));
};

/** Artifact node id is canvas `artifact-{uuid}`; live API uses raw artifact UUID. */
export const fetchArtifactActivity = async (
  artifactId: string,
): Promise<KgArtifactActivityResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchArtifactActivity(artifactId);
  }
  return kgDrawerService.getArtifactActivity(parseArtifactAssetId(artifactId));
};

/** Data model node id is canvas `dataModel-{uuid}` (mock: `dm-*`); live API uses raw UUID. */
export const fetchDataModelOverview = async (
  dataModelId: string,
): Promise<KgDataModelOverviewResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchDataModelOverview(dataModelId);
  }
  return kgDrawerService.getDataModelOverview(
    parseDataModelAssetId(dataModelId),
  );
};

/** Data model node id is canvas `dataModel-{uuid}` (mock: `dm-*`); live API uses raw UUID. */
export const fetchDataModelTables = async (
  dataModelId: string,
  params?: KgDrawerListParams,
): Promise<KgDataModelTablesResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchDataModelTables(dataModelId, params);
  }
  return kgDrawerService.getDataModelTables(
    parseDataModelAssetId(dataModelId),
    params,
  );
};

/** Data model node id is canvas `dataModel-{uuid}` (mock: `dm-*`); live API uses raw UUID. */
export const fetchDataModelMetrics = async (
  dataModelId: string,
  params?: KgDrawerListParams,
): Promise<KgDataModelMetricsResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchDataModelMetrics(dataModelId, params);
  }
  return kgDrawerService.getDataModelMetrics(
    parseDataModelAssetId(dataModelId),
    params,
  );
};

/** Data model node id is canvas `dataModel-{uuid}` (mock: `dm-*`); live API uses raw UUID. */
export const fetchDataModelRelationships = async (
  dataModelId: string,
  params?: KgDrawerListParams,
): Promise<KgDataModelRelationshipsResponse> => {
  if (isMockDrawerApi()) {
    return mockFetchDataModelRelationships(dataModelId, params);
  }
  return kgDrawerService.getDataModelRelationships(
    parseDataModelAssetId(dataModelId),
    params,
  );
};
